import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, Link } from "react-router-dom";
import AuthLayout from "../../layouts/AuthLayout";
import { authApi } from "../../api/api";
import { FiMail, FiLock, FiCheckCircle, FiArrowLeft } from "react-icons/fi";

interface ForgotPasswordFormData {
  email: string;
}

interface ResetPasswordFormData {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

const ForgotPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [step, setStep] = useState<"request" | "reset" | "success">(
    token ? "reset" : "request"
  );
  const [error, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  // Request Reset Link Form
  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: errorsRequest },
  } = useForm<ForgotPasswordFormData>();

  // Reset Password Form
  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      token: token || "",
      email: email || "",
    },
  });

  const password = watch("password");

  // Request Reset Link
  const onRequestReset = async (data: ForgotPasswordFormData) => {
    try {
      setError("");
      setIsSubmitting(true);
      const response = await authApi.post("/forgot-password", {
        email: data.email,
      });

      if (response.data.success || response.data.status) {
        setEmailSent(true);
      } else {
        setError(response.data.message || "Failed to send reset link");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to send reset link. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset Password
  const onResetPassword = async (data: ResetPasswordFormData) => {
    try {
      setError("");
      setIsSubmitting(true);

      const response = await authApi.post("/reset-password", {
        token: data.token,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      });

      if (response.data.success || response.data.status) {
        setStep("success");
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to reset password. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (step === "success") {
    return (
      <AuthLayout>
        <div className="w-full max-w-md mx-auto bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-200/80 text-center space-y-5">
          <div className="h-14 w-14 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center mx-auto shadow-sm">
            <FiCheckCircle className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Password Reset Successful</h2>
            <p className="text-xs text-slate-500 mt-1 font-medium">Your password has been changed. You can now sign in with your new credentials.</p>
          </div>
          <Link
            to="/login"
            className="w-full py-3 bg-[#064e3b] hover:bg-[#047857] text-white font-bold text-xs rounded-xl shadow-sm transition-all block"
          >
            Back to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Email Sent Confirmation
  if (emailSent && !token) {
    return (
      <AuthLayout>
        <div className="w-full max-w-md mx-auto bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-200/80 text-center space-y-5">
          <div className="h-14 w-14 bg-emerald-50 text-emerald-700 rounded-2xl flex items-center justify-center mx-auto shadow-sm">
            <FiMail className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Check Your Email</h1>
            <p className="text-xs text-slate-500 mt-1.5 font-medium leading-relaxed">
              We've dispatched a secure password reset link to your email address. Please follow the instructions in the link.
            </p>
          </div>
          <Link
            to="/login"
            className="w-full py-3 bg-[#064e3b] hover:bg-[#047857] text-white font-bold text-xs rounded-xl shadow-sm transition-all block"
          >
            Back to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Reset Password Form
  if (step === "reset" && token) {
    return (
      <AuthLayout>
        <div className="w-full max-w-lg mx-auto bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-200/80 space-y-6">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Set New Password</h1>
            <p className="text-xs text-slate-500 mt-1 font-medium">Choose a strong, unique password for your organization account.</p>
          </div>

          {error && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmitReset(onResetPassword)} className="space-y-4">
            <input type="hidden" {...registerReset("token")} />
            <input type="hidden" {...registerReset("email")} />

            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <FiLock className="w-4 h-4" />
                </div>
                <input
                  type="password"
                  {...registerReset("password", {
                    required: "Password is required",
                    minLength: { value: 8, message: "Password must be at least 8 characters" },
                  })}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 focus:border-slate-300"
                  placeholder="At least 8 characters"
                  disabled={isSubmitting}
                />
              </div>
              {errorsReset.password && (
                <p className="text-[11px] font-bold text-rose-500 mt-1">{errorsReset.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <FiLock className="w-4 h-4" />
                </div>
                <input
                  type="password"
                  {...registerReset("password_confirmation", {
                    required: "Please confirm your password",
                    validate: (value) => value === password || "Passwords do not match",
                  })}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 focus:border-slate-300"
                  placeholder="Re-enter your password"
                  disabled={isSubmitting}
                />
              </div>
              {errorsReset.password_confirmation && (
                <p className="text-[11px] font-bold text-rose-500 mt-1">{errorsReset.password_confirmation.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 bg-[#064e3b] hover:bg-[#047857] text-white font-extrabold text-xs rounded-xl shadow-md shadow-emerald-950/20 transition-all block mt-2"
            >
              {isSubmitting ? "Updating Password..." : "Reset Password"}
            </button>
          </form>

          <div className="pt-4 text-center border-t border-slate-100">
            <Link to="/login" className="text-xs font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
              <FiArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Request Reset Link Form Default
  return (
    <AuthLayout>
      <div className="w-full max-w-lg mx-auto bg-white rounded-3xl p-8 sm:p-10 shadow-2xl border border-slate-200/80 space-y-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Reset Password</h1>
          <p className="text-xs text-slate-500 mt-1 font-medium">Enter your registered email address and we will send you a recovery link.</p>
        </div>

        {error && (
          <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmitRequest(onRequestReset)} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1.5 tracking-wider">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <FiMail className="w-4 h-4" />
              </div>
              <input
                type="email"
                {...registerRequest("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                className="w-full pl-10 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-600/10 focus:border-slate-300"
                placeholder="name@company.com"
                disabled={isSubmitting}
              />
            </div>
            {errorsRequest.email && (
              <p className="text-[11px] font-bold text-rose-500 mt-1">{errorsRequest.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 bg-[#064e3b] hover:bg-[#047857] text-white font-extrabold text-xs rounded-xl shadow-md shadow-emerald-950/20 transition-all block mt-2"
          >
            {isSubmitting ? "Dispatching link..." : "Send Reset Link"}
          </button>
        </form>

        <div className="pt-4 text-center border-t border-slate-100">
          <Link to="/login" className="text-xs font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
            <FiArrowLeft className="w-3.5 h-3.5" /> Back to Login
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
